import React, { useState, useEffect, useRef } from 'react';
import {
  ChatContainer,
  ChatHeader,
  MessagesList,
  MessageItem,
  MessageBubble,
  MessageTime,
  InputArea,
  Input,
  SendButton,
  CloseButton,
} from './styles';
import { subscribeToConversation } from '../../services/connectionManager';
import axios from 'axios';

interface Message {
  id: number;
  sender_type: 'user' | 'admin' | 'system';
  content: string;
  created_at: string;
  status?: 'sending' | 'sent' | 'error';
  error?: string;
  tempId?: string; // ID temporário para mensagens sendo enviadas
}

interface ChatProps {
  conversationId: number | null;
  onClose: () => void;
}

const Chat: React.FC<ChatProps> = ({ conversationId, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Usar variável de ambiente ou URL relativa
  const API_URL = process.env.REACT_APP_API_URL || window.location.origin + '/api';

  // Refs para controlar WebSocket (usando refs para evitar re-renders desnecessários)
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isWebSocketConnectedRef = useRef(false);

  useEffect(() => {
    if (conversationId) {
      console.log('Chat: Carregando mensagens para conversa', conversationId);
      
      // Carregar histórico de mensagens
      const loadMessages = async () => {
        try {
          const response = await axios.get<Message[]>(
            `${API_URL}/messages?conversation_id=${conversationId}`,
            {
              withCredentials: true,
            }
          );
          setMessages(response.data);
          console.log('Chat: Histórico carregado,', response.data.length, 'mensagens');
        } catch (error) {
          console.error('Erro ao carregar mensagens:', error);
        }
      };
      
      loadMessages();
      
      // Inscrever na conversa usando Connection Manager (reutiliza conexão compartilhada)
      const subscribe = () => {
        console.log('Chat: Inscrevendo-se na conversa', conversationId, 'usando conexão compartilhada...');
        try {
          // Connection Manager garante que apenas UMA conexão WebSocket seja criada
          // e reutilizada para todos os chats
          const unsub = subscribeToConversation(conversationId, (message: any) => {
            console.log('Chat: Mensagem recebida via WebSocket compartilhado:', message);
            
            // Processar mensagem recebida
            setMessages((prev) => {
              // CRÍTICO: Evitar duplicatas - verificar apenas pelo ID
              // Como não adicionamos mensagem otimista, não precisamos verificar tempId
              const existingById = prev.find((m) => m.id === message.id && m.id !== 0);
              if (existingById) {
                console.log('Chat: Mensagem duplicada ignorada (já existe pelo ID):', message.id);
                return prev;
              }
              
              // Adicionar mensagem recebida via websocket
              // Isso inclui tanto mensagens que enviamos quanto mensagens recebidas
              console.log('Chat: Adicionando nova mensagem recebida via WebSocket:', message.id, message.content);
              return [...prev, { ...message, status: 'sent' as const }];
            });
          });
          
          unsubscribeRef.current = unsub;
          isWebSocketConnectedRef.current = true;
          console.log('Chat: Inscrito na conversa', conversationId, 'usando conexão compartilhada');
        } catch (error) {
          console.error('Chat: Erro ao se inscrever na conversa:', error);
          isWebSocketConnectedRef.current = false;
        }
      };
      
      subscribe();
      
      return () => {
        // Apenas desinscrever da conversa quando chat fecha (não desconectar WebSocket global)
        if (unsubscribeRef.current) {
          console.log('Chat: Desinscrevendo da conversa', conversationId);
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        isWebSocketConnectedRef.current = false;
      };
    } else {
      // Limpar mensagens quando não há conversa
      setMessages([]);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      isWebSocketConnectedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || !conversationId || loading) return;

    // Validar e sanitizar conteúdo
    let content = inputValue.trim();
    
    // Limitar tamanho da mensagem (5000 caracteres)
    if (content.length > 5000) {
      alert('Mensagem muito longa. Máximo de 5000 caracteres.');
      return;
    }
    
    // Remover caracteres de controle perigosos (mas manter quebras de linha)
    // eslint-disable-next-line no-control-regex
    content = content.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');
    
    setInputValue('');
    setLoading(true);

    try {
      // Enviar mensagem via API
      // CRÍTICO: NÃO adicionar mensagem otimista - deixar o websocket adicionar
      // Isso evita duplicação e garante que a mensagem só aparece quando realmente foi enviada
      console.log('Chat: Enviando mensagem via POST para', `${API_URL}/messages`);
      const response = await axios.post<Message>(
        `${API_URL}/messages`,
        {
          conversation_id: conversationId,
          sender_type: 'user',
          content: content,
        },
        {
          withCredentials: true,
        }
      );
      console.log('Chat: Mensagem enviada com sucesso, resposta:', response.data);
      
      // NÃO adicionar mensagem aqui - o websocket vai receber e adicionar automaticamente
      // Isso garante que a mensagem só aparece quando realmente foi broadcastada
      // Se o websocket falhar, a mensagem não aparecerá (mas foi salva no backend)
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      
      // Como não adicionamos mensagem otimista, apenas logar o erro
      // A mensagem não foi salva no backend, então não aparecerá na lista
      // O usuário pode tentar enviar novamente
      alert('Erro ao enviar mensagem: ' + (error.response?.data?.error || error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const retryMessage = async (message: Message) => {
    if (!conversationId || !message.id) {
      return;
    }

    const messageContent = message.content;

    // Remover mensagem com erro e tentar enviar novamente
    setMessages((prev) => prev.filter((m) => m.id !== message.id));

    try {
      // Enviar mensagem via API novamente
      const response = await axios.post<Message>(
        `${API_URL}/messages`,
        {
          conversation_id: conversationId,
          sender_type: 'user',
          content: messageContent,
        },
        {
          withCredentials: true,
        }
      );

      console.log('Chat: Mensagem reenviada com sucesso, resposta:', response.data);
      
      // Não adicionar mensagem aqui - o websocket vai receber e adicionar
    } catch (error) {
      console.error('Erro ao reenviar mensagem:', error);
      alert('Erro ao reenviar mensagem: ' + (error.response?.data?.error || error.message || 'Erro desconhecido'));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!conversationId) return null;

  return (
    <ChatContainer>
      <ChatHeader>
        <h3><span role="img" aria-label="Chat">💬</span> Chat de Suporte</h3>
        <CloseButton onClick={onClose}>×</CloseButton>
      </ChatHeader>

      <MessagesList>
        {messages.map((message) => (
          <MessageItem key={message.tempId || message.id} senderType={message.sender_type}>
            <MessageBubble senderType={message.sender_type}>
              {message.content}
              {message.status === 'sending' && (
                <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.7 }}>
                  Enviando...
                </span>
              )}
              {message.status === 'error' && (
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#d32f2f' }}>
                  <span>{message.error || 'Erro ao enviar'}</span>
                  <button
                    onClick={() => retryMessage(message)}
                    style={{
                      marginLeft: '8px',
                      padding: '2px 8px',
                      fontSize: '11px',
                      background: '#d32f2f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    <span role="img" aria-label="Tentar novamente">🔄</span> Tentar novamente
                  </button>
                </div>
              )}
            </MessageBubble>
            <MessageTime>
              {message.status === 'sending' ? (
                <span style={{ opacity: 0.5 }}>Enviando...</span>
              ) : message.status === 'error' ? (
                <span style={{ color: '#d32f2f' }}>Erro</span>
              ) : (
                new Date(message.created_at).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              )}
            </MessageTime>
          </MessageItem>
        ))}
        <div ref={messagesEndRef} />
      </MessagesList>

      <InputArea>
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Digite sua mensagem..."
          disabled={loading}
        />
        <SendButton onClick={sendMessage} disabled={loading || !inputValue.trim()}>
          Enviar
        </SendButton>
      </InputArea>
    </ChatContainer>
  );
};

export default Chat;
